import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Tarjeta } from '../componentes/ui/basicos';
import { Cargando, Problema, Vacio } from '../componentes/ui/estados';
import { Tabla, type Columna } from '../componentes/ui/tabla';
import { usePermiso } from '../sesion/ProveedorDeSesion';
import { DialogoDePago } from './DialogoDePago';

interface CuentaDeProveedor {
  readonly terceroId: string;
  readonly razonSocial: string;
  readonly identificacion: string | null;
  readonly saldo: string;
}

function conDosDecimales(importe: string): string {
  const [entera, fraccion = ''] = importe.split('.');
  return `${entera ?? '0'}.${fraccion.padEnd(2, '0').slice(0, 2)}`;
}

/**
 * Pagos a proveedores: el espejo de la cobranza.
 *
 * Es una pantalla propia y no una pestaña de cuentas corrientes porque **quien cobra y quien paga
 * no suelen ser la misma persona**, y porque lo que se mira es distinto: acá interesa qué vence
 * primero, no cuánto debe cada uno.
 */
export function Pagos(): React.ReactElement {
  const { t } = useTranslation();

  const puedePagar = usePermiso('CUENTAS.PAGO.REGISTRAR');

  const [pagando, setPagando] = useState<CuentaDeProveedor | null>(null);

  const consulta = useQuery({
    queryKey: ['cuentas-de-proveedores'],
    queryFn: () => pedir<CuentaDeProveedor[]>('/cuentas-corrientes?plano=proveedor'),
  });

  const columnas: Columna<CuentaDeProveedor>[] = [
    { clave: 'razonSocial', titulo: t('pagos.proveedor'), contenido: (fila) => fila.razonSocial },
    {
      clave: 'identificacion',
      titulo: t('cuentas.identificacion'),
      contenido: (fila) => <span className="numero">{fila.identificacion ?? '—'}</span>,
    },
    {
      clave: 'saldo',
      titulo: t('cuentas.saldo'),
      numerica: true,
      contenido: (fila) => <span className="numero">{conDosDecimales(fila.saldo)}</span>,
    },
    {
      clave: 'acciones',
      titulo: t('cuentas.acciones'),
      contenido: (fila) =>
        puedePagar ? (
          <Boton
            tono="principal"
            onClick={() => {
              setPagando(fila);
            }}
          >
            {t('pagos.pagar')}
          </Boton>
        ) : null,
    },
  ];

  return (
    <>
      <Encabezado titulo={t('pagos.titulo')} descripcion={t('pagos.descripcion')} />

      <Tarjeta>
        {consulta.isPending ? <Cargando /> : null}
        {consulta.isError ? (
          <Problema
            error={consulta.error}
            reintentar={() => {
              void consulta.refetch();
            }}
          />
        ) : null}
        {consulta.isSuccess ? (
          <Tabla
            columnas={columnas}
            filas={consulta.data}
            claveDeFila={(fila) => fila.terceroId}
            vacio={<Vacio descripcion={t('pagos.nadieCobra')} />}
          />
        ) : null}
      </Tarjeta>

      <DialogoDePago
        proveedor={pagando}
        onCerrar={() => {
          setPagando(null);
        }}
      />
    </>
  );
}
